/**
 * Deal-screening read/write contract (DOP-494..499).
 *
 * Pins the JSON `/api/deal-screening` serves, field for field, from
 * `backend/app/modules/deal_screening/schemas/deal_screening.py`. The wire
 * values of every vocabulary are the backend enums' strings; a value the
 * server starts sending that is not listed here is a contract change and fails
 * the parse rather than rendering as something the page does not understand.
 *
 * Parsed with Zod at the boundary (`zod/v3`, like `types/monitoring.ts`).
 * Unlike monitoring, timestamps are ISO-8601 strings (pydantic `datetime`),
 * ids are public ids, and money is USD millions (`unit: "usd_m"`).
 */

import { z } from "zod/v3";

/** What a reader may do with a deal or a memo: the owner's everything, a
 * teammate's edit or view (the deal's folder share). */
export const DealScreeningAccessSchema = z.enum(["owner", "edit", "view"]);
export type DealScreeningAccess = z.infer<typeof DealScreeningAccessSchema>;


// ----------------------------------------------------------------- vocabularies

/** `blocks` stops "Mark reviewed" while a gap; `required` raises a question. */
export const DealScreeningPolicySchema = z.enum([
  "blocks",
  "required",
  "optional",
]);
export type DealScreeningPolicy = z.infer<typeof DealScreeningPolicySchema>;

/** A person may enter `header` and `input` facts; `derived` ones are computed. */
export const DealScreeningFactKindSchema = z.enum([
  "header",
  "input",
  "derived",
]);
export type DealScreeningFactKind = z.infer<typeof DealScreeningFactKindSchema>;

export const DealScreeningFactStateSchema = z.enum([
  "found",
  "derived",
  "manual",
  "gap",
  "not_available",
]);
export type DealScreeningFactState = z.infer<
  typeof DealScreeningFactStateSchema
>;

export const DealScreeningRunStatusSchema = z.enum(["draft", "reviewed"]);
export type DealScreeningRunStatus = z.infer<
  typeof DealScreeningRunStatusSchema
>;

export const DealScreeningSectionStatusSchema = z.enum(["written", "omitted"]);
export type DealScreeningSectionStatus = z.infer<
  typeof DealScreeningSectionStatusSchema
>;

export const DealScreeningQuestionOriginSchema = z.enum([
  "gap",
  "conflict",
  "omitted",
  "analyst",
]);
export type DealScreeningQuestionOrigin = z.infer<
  typeof DealScreeningQuestionOriginSchema
>;

/** How a file was read. `ocr` is reserved: v1 never produces it (DOP-500). */
export const DealScreeningReadStatusSchema = z.enum([
  "text",
  "ocr",
  "unreadable",
  "skipped",
]);
export type DealScreeningReadStatus = z.infer<
  typeof DealScreeningReadStatusSchema
>;

export const DealScreeningConflictStateSchema = z.enum(["open", "resolved"]);
export type DealScreeningConflictState = z.infer<
  typeof DealScreeningConflictStateSchema
>;

/** A fact's value: a number in its unit, a text value, or nothing at all. */
const FactValueSchema = z.union([z.number(), z.string()]).nullable();
export type DealScreeningFactValue = z.infer<typeof FactValueSchema>;

const CountsSchema = z.record(z.string(), z.number());

// ----------------------------------------------------------------- recipe

export const DealScreeningHeaderFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  policy: DealScreeningPolicySchema,
  unit: z.string(),
  definition: z.string().nullable(),
  /** Fields sharing a group unblock together: any one member satisfies it. */
  block_group: z.string().nullable(),
  /** The Analyst's classification from cited facts (sector), not a verbatim read. */
  classified: z.boolean(),
  enabled: z.boolean(),
  sort_order: z.number(),
  /** Which ranking of document types applies to this field (DOP-578). */
  category: z.string().optional(),
});
export type DealScreeningHeaderField = z.infer<
  typeof DealScreeningHeaderFieldSchema
>;

export const DealScreeningFigureSpecSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: DealScreeningFactKindSchema,
  unit: z.string(),
  policy: DealScreeningPolicySchema,
  /** "by_year" | "point" | "none" — a product vocabulary, kept a string. */
  periods: z.string(),
  definition: z.string().nullable(),
  formula: z.string().nullable(),
  inputs: z.array(z.string()),
  enabled: z.boolean(),
  sort_order: z.number(),
  /** False for a figure the run reads but the summary never prints (DOP-576). */
  printed: z.boolean().optional(),
  fallback_formula: z.string().nullable().optional(),
  fallback_inputs: z.array(z.string()).optional(),
  category: z.string().optional(),
});
export type DealScreeningFigureSpec = z.infer<
  typeof DealScreeningFigureSpecSchema
>;

/** Which document type is most authoritative for which kind of information (DOP-578). */
export const DealScreeningRankingSchema = z.object({
  doc_type_order: z.record(z.string(), z.array(z.string())),
  recency_wins: z.array(z.string()),
  recency_margin_days: z.number(),
});
export type DealScreeningRanking = z.infer<typeof DealScreeningRankingSchema>;

/** An anomaly the recipe watches for: a ratio with a threshold (DOP-576). */
export const DealScreeningSignalSpecSchema = z.object({
  key: z.string(),
  label: z.string(),
  formula: z.string(),
  inputs: z.array(z.string()),
  threshold: z.number(),
  direction: z.string(),
  note: z.string(),
  unit: z.string(),
  enabled: z.boolean(),
  sort_order: z.number(),
});
export type DealScreeningSignalSpec = z.infer<
  typeof DealScreeningSignalSpecSchema
>;

/** A signal evaluated over a run's facts (DOP-576). */
export const DealScreeningSignalSchema = z.object({
  key: z.string(),
  label: z.string(),
  formula: z.string(),
  unit: z.string(),
  value: z.number().nullable(),
  fired: z.boolean(),
  threshold: z.number(),
  direction: z.string(),
  note: z.string(),
  inputs: z.array(z.string()),
  operands: z.array(
    z.object({
      key: z.string(),
      period: z.string().nullable(),
      value: z.number(),
    }),
  ),
  unavailable: z.string().nullable(),
});
export type DealScreeningSignal = z.infer<typeof DealScreeningSignalSchema>;

export const DealScreeningSectionSpecSchema = z.object({
  key: z.string(),
  title: z.string(),
  brief: z.string(),
  /** "always" | "if_sourced". */
  appears: z.string(),
  /** "model" (prose from cited passages) | "code" (tables, source note). */
  written_by: z.string(),
  tone: z.string(),
  length: z.string().nullable(),
  sub_headings: z.array(z.string()),
  deal_specific: z.boolean(),
  /** Deal-specific sections: inserted after this section key. */
  after: z.string().nullable(),
  enabled: z.boolean(),
  sort_order: z.number(),
});
export type DealScreeningSectionSpec = z.infer<
  typeof DealScreeningSectionSpecSchema
>;

export const DealScreeningRecipeSchema = z.object({
  version: z.number(),
  header: z.array(DealScreeningHeaderFieldSchema),
  figures: z.array(DealScreeningFigureSpecSchema),
  sections: z.array(DealScreeningSectionSpecSchema),
  deal_specific_sections: z.array(DealScreeningSectionSpecSchema),
  signals: z.array(DealScreeningSignalSpecSchema).optional(),
  ranking: DealScreeningRankingSchema.nullable().optional(),
  conventions: z.record(z.string(), z.string()),
});
export type DealScreeningRecipe = z.infer<typeof DealScreeningRecipeSchema>;

// ----------------------------------------------------------------- sources & facts

/**
 * Where a value or a sentence was read. Exactly one of `page` or
 * (`sheet`, `cell`) is set; `anchor` is the `doppler://` URI the viewer opens.
 */
export const DealScreeningSourceSchema = z.object({
  attachment_id: z.number(),
  filename: z.string(),
  doc_type: z.string(),
  quote: z.string(),
  anchor: z.string(),
  verified: z.boolean(),
  page: z.number().nullable(),
  sheet: z.string().nullable(),
  cell: z.string().nullable(),
  ocr: z.boolean(),
  /** The date the document is of, when one was read (DOP-578). */
  doc_date: z.string().nullable().optional(),
  /** document | web | user (DOP-581); a web source links out to `url`. */
  kind: z.string().optional(),
  url: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  retrieved_at: z.string().nullable().optional(),
  /** A person's note on where their words came from (`kind` "user", DOP-637). */
  note: z.string().nullable().optional(),
});
export type DealScreeningSource = z.infer<typeof DealScreeningSourceSchema>;

/**
 * One month, quarter or funding line a summed figure was added up from, with
 * its own value (in the figure's unit) and the cell or page it was read at.
 */
export const DealScreeningPartSchema = z.object({
  period: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  value: z.number().nullable().optional(),
  source: DealScreeningSourceSchema.nullable().optional(),
});
export type DealScreeningPart = z.infer<typeof DealScreeningPartSchema>;

/** Another source that answered the same fact with a DIFFERENT value. */
export const DealScreeningAlternateSchema = z.object({
  value: FactValueSchema,
  unit: z.string(),
  source: DealScreeningSourceSchema,
  // An alternate is a found fact in its own right: its own currency and
  // conversion. Optional (not defaulted) so the schema's input and output
  // types stay one type: a run stored before these fields existed parses too.
  projected: z.boolean().optional(),
  original_currency: z.string().nullable().optional(),
  original_value: z.number().nullable().optional(),
  fx_rate: z.number().nullable().optional(),
  fx_as_of: z.string().nullable().optional(),
  /** A reading at another date is dated, not disputed (DOP-530). */
  period: z.string().nullable().optional(),
  period_end: z.string().nullable().optional(),
});
export type DealScreeningAlternate = z.infer<
  typeof DealScreeningAlternateSchema
>;

export const DealScreeningRecommendationSchema = z.object({
  anchor: z.string(),
  attachment_id: z.number(),
  value: FactValueSchema.optional(),
  reasons: z.array(z.string()),
});
export type DealScreeningRecommendation = z.infer<
  typeof DealScreeningRecommendationSchema
>;

export const DealScreeningDecisionKindSchema = z.enum([
  "choose_source",
  "enter_value",
  "accept_na",
  "clear",
]);
export type DealScreeningDecisionKind = z.infer<
  typeof DealScreeningDecisionKindSchema
>;
export const DealScreeningDecisionStatusSchema = z.enum([
  "fresh",
  "carried",
  "stale",
  "orphaned",
]);
export type DealScreeningDecisionStatus = z.infer<
  typeof DealScreeningDecisionStatusSchema
>;

/** A reading the person saw when deciding (DOP-577). */
export const DealScreeningDecisionCandidateSchema = z.object({
  anchor: z.string(),
  attachment_id: z.number(),
  filename: z.string(),
  doc_type: z.string(),
  page: z.number().nullable(),
  sheet: z.string().nullable(),
  cell: z.string().nullable(),
  quote: z.string(),
  value: FactValueSchema,
  unit: z.string(),
  period: z.string().nullable(),
});

/** One act of a person on one fact of the deal; `active` is what the memo projects (DOP-577). */
export const DealScreeningDecisionSchema = z.object({
  id: z.string(),
  key: z.string(),
  period: z.string().nullable(),
  kind: DealScreeningDecisionKindSchema,
  value: FactValueSchema,
  unit: z.string().nullable(),
  note: z.string(),
  chosen_anchor: z.string().nullable(),
  chosen_attachment_id: z.number().nullable(),
  candidates: z.array(DealScreeningDecisionCandidateSchema),
  recommended: DealScreeningRecommendationSchema.nullable(),
  created_by_user_id: z.string(),
  created_by_name: z.string().nullable(),
  created_at: z.string(),
  active: z.boolean(),
  superseded_by_id: z.string().nullable(),
  origin_run_version: z.number().nullable(),
});
export type DealScreeningDecision = z.infer<typeof DealScreeningDecisionSchema>;

export const DealScreeningFactSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: DealScreeningFactKindSchema,
  policy: DealScreeningPolicySchema,
  state: DealScreeningFactStateSchema,
  value: FactValueSchema,
  unit: z.string(),
  /** "FY2025A" / "FY2026P"; null for header and point figures. */
  period: z.string().nullable(),
  projected: z.boolean(),
  verified: z.boolean(),
  source: DealScreeningSourceSchema.nullable(),
  /** The parts a figure the server summed was added up from; each opens its own cell. */
  parts: z.array(DealScreeningPartSchema).optional(),
  alternates: z.array(DealScreeningAlternateSchema),
  conflict: DealScreeningConflictStateSchema.nullable(),
  /** User ids are UUID strings. */
  resolved_by_user_id: z.string().nullable(),
  formula: z.string().nullable(),
  inputs: z.array(z.string()),
  /** Where the run looked, in plain words — set on a gap. */
  gap_reason: z.string().nullable(),
  entered_by_user_id: z.string().nullable(),
  /** Who typed a manual value; the memo prints "entered by <name>". */
  entered_by_name: z.string().nullable(),
  entered_at: z.string().nullable(),
  note: z.string().nullable(),
  block_group: z.string().nullable(),
  /** A converted value keeps its original amount, currency, rate and date. */
  original_currency: z.string().nullable(),
  original_value: z.number().nullable(),
  fx_rate: z.number().nullable(),
  /** ISO `YYYY-MM-DD` (pydantic `date`). */
  fx_as_of: z.string().nullable(),
  /** The as-of date of a point-in-time reading (cash, debt, equity): "Latest" is the newest (DOP-530). */
  period_end: z.string().nullable().optional(),
  /** A header value the Analyst classified from cited facts (sector), not read verbatim. */
  classified: z.boolean(),
  sort_order: z.number(),
  /** False for a figure read for a signal or a formula, never printed (DOP-576). */
  printed: z.boolean().optional(),
  /** The system's pick among the candidates and why (DOP-577). */
  recommended: DealScreeningRecommendationSchema.nullable().optional(),
  /** The active decision projected onto this fact, and how it stands (DOP-577). */
  decision_id: z.string().nullable().optional(),
  decision_status: DealScreeningDecisionStatusSchema.nullable().optional(),
  stale_reason: z.string().nullable().optional(),
});
export type DealScreeningFact = z.infer<typeof DealScreeningFactSchema>;

// ----------------------------------------------------------------- memo

/** A run of a sentence's text, `[start, end)`, in one style (DOP-641). */
export const DealScreeningFormatSpanSchema = z.object({
  start: z.number(),
  end: z.number(),
  style: z.enum(["bold", "italic", "underline", "strike"]),
});
export type DealScreeningFormatSpan = z.infer<
  typeof DealScreeningFormatSpanSchema
>;
export type DealScreeningFormatStyle = DealScreeningFormatSpan["style"];

export const DealScreeningSentenceSchema = z.object({
  /** Stable within a run version: "<section_key>:<paragraph>:<sentence>". */
  id: z.string(),
  text: z.string(),
  source: DealScreeningSourceSchema.nullable(),
  /** The facts whose values appear in the sentence; the page underlines them. */
  fact_keys: z.array(z.string()),
  /** A person's sentence (DOP-580): no citation, shown as theirs. */
  edited: z.boolean().optional(),
  /** Bold, italic, underline or strikethrough a person gave its words (DOP-641). */
  format: z.array(DealScreeningFormatSpanSchema).optional(),
});
export type DealScreeningSentence = z.infer<typeof DealScreeningSentenceSchema>;

/** One table cell, verified like a sentence; a label cell has no source. */
export const DealScreeningCellSchema = z.object({
  text: z.string(),
  source: DealScreeningSourceSchema.nullable(),
  fact_keys: z.array(z.string()),
  /** A cell a person typed into a table they edited (DOP-637): theirs, no source. */
  edited: z.boolean().optional(),
});
export type DealScreeningCell = z.infer<typeof DealScreeningCellSchema>;

/** A table the composer wrote from the materials (market sizing, revenue streams, holdings). */
export const DealScreeningTableSchema = z.object({
  caption: z.string().nullable(),
  columns: z.array(z.string()),
  rows: z.array(z.array(DealScreeningCellSchema)),
  /** Rows verification removed, or rows that do not add up to the Total. */
  note: z.string().nullable().optional(),
});
export type DealScreeningTable = z.infer<typeof DealScreeningTableSchema>;

/**
 * A chart a person or the memo chat put in a section (DOP-655), in the shape
 * the chat's own charts take: `chart_type` with `data.labels`/`data.series`,
 * or `data.segments` for a pie. `sources` are where its values came from.
 */
export const DealScreeningChartSchema = z.object({
  chart_type: z.string(),
  title: z.string().nullable().optional(),
  data: z.record(z.string(), z.unknown()),
  sources: z.array(DealScreeningSourceSchema).optional(),
});
export type DealScreeningChart = z.infer<typeof DealScreeningChartSchema>;

export const DealScreeningParagraphKindSchema = z.enum([
  "prose",
  "bullets",
  "table",
  "chart",
]);
export type DealScreeningParagraphKind = z.infer<
  typeof DealScreeningParagraphKindSchema
>;

/** A paragraph a person wrote or rewrote (DOP-580). */
export const DealScreeningEditMarkSchema = z.object({
  op: z.enum(["insert", "replace"]),
  by_user_id: z.string(),
  by_name: z.string().nullable(),
  by_email: z.string().nullable().optional(),
  at: z.string(),
});
export type DealScreeningEditMark = z.infer<typeof DealScreeningEditMarkSchema>;

export const DealScreeningParagraphSchema = z.object({
  sentences: z.array(DealScreeningSentenceSchema),
  /** Section 8 bullets: the bold lead-in. */
  lead: z.string().nullable(),
  /** "analyst" on a strength or risk that is the writer's judgement over
   * cited facts (DOP-657). Recorded only; the page prints no label for it. */
  basis: z.string().nullable().optional(),
  sub_heading: z.string().nullable(),
  /** prose (default) | bullets (one per sentence) | table (no sentences). A run stored before DOP-533 carries none. */
  kind: DealScreeningParagraphKindSchema.optional(),
  /** A bullets paragraph a person numbered (DOP-641). */
  ordered: z.boolean().optional(),
  table: DealScreeningTableSchema.nullable().optional(),
  /** A chart paragraph carries no sentences; the chart is the paragraph (DOP-655). */
  chart: DealScreeningChartSchema.nullable().optional(),
  /** Set on a paragraph a person wrote; its sentences have no verified source (DOP-580). */
  edit: DealScreeningEditMarkSchema.nullable().optional(),
  /** The generated paragraph's text this one stands for; null on a person's addition (DOP-580). */
  anchor: z.string().nullable().optional(),
  /** The lead-in is the person's: written or changed by them (DOP-580). */
  lead_edited: z.boolean().optional(),
});

/** The active prose edit on a section (DOP-580). */
export const DealScreeningProseEditSchema = z.object({
  id: z.string(),
  edited_by_user_id: z.string(),
  edited_by_name: z.string().nullable(),
  edited_by_email: z.string().nullable().optional(),
  edited_at: z.string(),
  carried_from_version: z.number().nullable(),
  notes: z.array(z.string()),
});
export type DealScreeningProseEdit = z.infer<
  typeof DealScreeningProseEditSchema
>;
export type DealScreeningParagraph = z.infer<
  typeof DealScreeningParagraphSchema
>;

export const DealScreeningSectionSchema = z.object({
  key: z.string(),
  /** As a person renamed it, else as generated (DOP-641). */
  title: z.string(),
  /** The title the run gave it, which a rename is written against. */
  generated_title: z.string().nullable().optional(),
  status: DealScreeningSectionStatusSchema,
  order: z.number(),
  paragraphs: z.array(DealScreeningParagraphSchema),
  omitted_reason: z.string().nullable(),
  /** The recipe's "When it appears" for this section: "always" |
   * "if_sourced" (DOP-657). Omitted + "always" + no paragraph with words,
   * a table or a chart (a rename alone writes nothing) = a required section
   * still to be written; the backend refuses review while it stands. */
  appears: z.string().nullable().optional(),
  /** The server's verdict (DOP-657): a section that always appears and
   * shows nothing — never generated, or emptied by an edit. Blocks review. */
  required_unwritten: z.boolean().optional(),
  deal_specific: z.boolean(),
  written_by: z.string(),
  /** DOP-580: the active edit, the generated paragraphs under it, and the body's fingerprint. */
  edit: DealScreeningProseEditSchema.nullable().optional(),
  generated_paragraphs: z
    .array(DealScreeningParagraphSchema)
    .nullable()
    .optional(),
  base_body_sha: z.string().nullable().optional(),
});
export type DealScreeningSection = z.infer<typeof DealScreeningSectionSchema>;

export const DealScreeningQuestionSchema = z.object({
  topic: z.string(),
  text: z.string(),
  origin: DealScreeningQuestionOriginSchema,
  critical: z.boolean(),
  fact_key: z.string().nullable(),
  section_key: z.string().nullable(),
  source: DealScreeningSourceSchema.nullable(),
  sort_order: z.number(),
});
export type DealScreeningQuestion = z.infer<typeof DealScreeningQuestionSchema>;

// ----------------------------------------------------------------- run report

export const DealScreeningFileReadSchema = z.object({
  attachment_id: z.number(),
  filename: z.string(),
  doc_type: z.string(),
  family: z.string(),
  read_status: DealScreeningReadStatusSchema,
  pages: z.number(),
  sheets: z.number(),
  note: z.string().nullable(),
  doc_date: z.string().nullable().optional(),
  /** A web page's address (DOP-645); absent for a file and on older runs. */
  url: z.string().nullable().optional(),
});
export type DealScreeningFileRead = z.infer<typeof DealScreeningFileReadSchema>;

export const DealScreeningCheckSchema = z.object({
  name: z.string(),
  passed: z.boolean(),
  detail: z.string(),
});
export type DealScreeningCheck = z.infer<typeof DealScreeningCheckSchema>;

export const DealScreeningReportSchema = z.object({
  files: z.array(DealScreeningFileReadSchema),
  checks: z.array(DealScreeningCheckSchema),
  /**
   * header_found, header_total, figures_found, figures_total, sentences,
   * sentences_sourced, conflicts, gaps, blocking, sections_written,
   * sections_omitted (contracts.RunReport).
   */
  counts: CountsSchema,
});
export type DealScreeningReport = z.infer<typeof DealScreeningReportSchema>;

// ----------------------------------------------------------------- workflow, deal, run

export const DealScreeningWorkflowRefSchema = z.object({
  public_id: z.string(),
  name: z.string(),
});
export type DealScreeningWorkflowRef = z.infer<
  typeof DealScreeningWorkflowRefSchema
>;

export const DealScreeningDealRefSchema = z.object({
  public_id: z.string(),
  name: z.string(),
  folder_id: z.number(),
  folder_name: z.string().nullable(),
});
export type DealScreeningDealRef = z.infer<typeof DealScreeningDealRefSchema>;

export const DealScreeningRunSummarySchema = z.object({
  public_id: z.string(),
  workflow: DealScreeningWorkflowRefSchema,
  deal: DealScreeningDealRefSchema,
  version: z.number(),
  status: DealScreeningRunStatusSchema,
  generating: z.boolean(),
  generated_at: z.string().nullable(),
  generation_error: z.string().nullable(),
  reviewed_at: z.string().nullable(),
  reviewed_by_user_id: z.string().nullable(),
  /** Mirrors `report.counts`, so a board row needs no run body. */
  counts: CountsSchema,
  blocking_open: z.number(),
  conflicts_open: z.number(),
  gaps_open: z.number(),
  model: z.string().nullable(),
  cost_usd: z.number().nullable(),
  created_at: z.string(),
  /** "generated" by a run, or "edited": a person's edits saved as a version (DOP-621). */
  origin: z.string().optional(),
  name: z.string().nullable().optional(),
});
export type DealScreeningRunSummary = z.infer<
  typeof DealScreeningRunSummarySchema
>;

/** A row the financial summary can carry, as the run's recipe names it (DOP-637). */
export const DealScreeningRunFigureSchema = z.object({
  key: z.string(),
  label: z.string(),
  unit: z.string(),
  kind: z.enum(["input", "derived"]),
  periods: z.enum(["by_year", "point"]),
  /** Added by a person to this deal, not listed in the settings. */
  custom: z.boolean(),
});
export type DealScreeningRunFigure = z.infer<
  typeof DealScreeningRunFigureSchema
>;

export const DealScreeningRunSchema = DealScreeningRunSummarySchema.extend({
  recipe_version: z.number(),
  /** The deal's name as THIS version carries it (DOP-659); `deal.name` is the
   * deal's current one. Null on a version saved before names were kept per
   * version: read it through `memoDealName`. */
  deal_name: z.string().nullable().optional(),
  /** The deal's newest version when this one was read; a save sends it back (DOP-653). */
  latest_version: z.number().optional(),
  /** That newest version's id, to open it from an earlier one (DOP-653). */
  latest_run_id: z.string().nullable().optional(),
  /** What the reader may do with this memo (shared memos). */
  my_access: DealScreeningAccessSchema.optional(),
  /** The tenant's display name for the title block; null when unset. */
  firm_name: z.string().nullable(),
  facts: z.array(DealScreeningFactSchema),
  sections: z.array(DealScreeningSectionSchema),
  questions: z.array(DealScreeningQuestionSchema),
  report: DealScreeningReportSchema,
  conventions: z.record(z.string(), z.string()),
  signals: z.array(DealScreeningSignalSchema).optional(),
  figures: z.array(DealScreeningRunFigureSchema).optional(),
  /** Every decision on the deal, oldest first (DOP-577). */
  decisions: z.array(DealScreeningDecisionSchema).optional(),
  /** The web pages this run read to fill gaps (DOP-581). */
  web_sources: z
    .array(
      z.object({
        id: z.string(),
        key: z.string(),
        query: z.string(),
        url: z.string(),
        title: z.string(),
        quote: z.string(),
        value: FactValueSchema,
        unit: z.string(),
        verified: z.boolean(),
        retrieved_at: z.string(),
      }),
    )
    .optional(),
});
export type DealScreeningRun = z.infer<typeof DealScreeningRunSchema>;

export const DealScreeningDealSchema = z.object({
  public_id: z.string(),
  name: z.string(),
  folder_id: z.number(),
  folder_name: z.string().nullable(),
  file_count: z.number(),
  sort_order: z.number(),
  /** Deal-specific section keys this deal's memos leave out. */
  excluded_section_keys: z.array(z.string()),
  latest_run: DealScreeningRunSummarySchema.nullable(),
  created_at: z.string(),
  /** Whose deal it is, and what the reader may do with it (shared memos). */
  owner_user_id: z.string().nullable().optional(),
  owner_name: z.string().nullable().optional(),
  my_access: DealScreeningAccessSchema.optional(),
});
export type DealScreeningDeal = z.infer<typeof DealScreeningDealSchema>;

export const DealScreeningWorkflowSchema = z.object({
  public_id: z.string(),
  name: z.string(),
  kind: z.string(),
  owner_user_id: z.string(),
  recipe: DealScreeningRecipeSchema,
  deals_count: z.number(),
  runs_count: z.number(),
  latest_run_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type DealScreeningWorkflow = z.infer<typeof DealScreeningWorkflowSchema>;

export const DealScreeningWorkflowListItemSchema = z.object({
  public_id: z.string(),
  name: z.string(),
  kind: z.string(),
  deals_count: z.number(),
  memo_drafts: z.number(),
  deals_not_run: z.number(),
  blocking_open: z.number(),
  latest_run_at: z.string().nullable(),
  created_at: z.string(),
  /** The index row's "recipe vN". */
  recipe_version: z.number(),
});
export type DealScreeningWorkflowListItem = z.infer<
  typeof DealScreeningWorkflowListItemSchema
>;

/** One Deal Room folder a deal can point at (`GET /workflows/{id}/folder-candidates`). */
export const DealScreeningFolderCandidateSchema = z.object({
  folder_id: z.number(),
  name: z.string(),
  file_count: z.number(),
});
export type DealScreeningFolderCandidate = z.infer<
  typeof DealScreeningFolderCandidateSchema
>;

/** The 202 body: the run created, or the one already in flight. */
export const DealScreeningRunRequestSchema = z.object({
  run: DealScreeningRunSummarySchema,
  queued: z.boolean(),
});
export type DealScreeningRunRequest = z.infer<
  typeof DealScreeningRunRequestSchema
>;

/** One person's part of a saved version: their changes, by section. */
export const DealScreeningContributorSchema = z.object({
  user_id: z.string(),
  name: z.string().nullable().optional(),
  changes: z.number(),
  sections: z.record(z.string(), z.number()),
});
export type DealScreeningContributor = z.infer<typeof DealScreeningContributorSchema>;

/** The deal's shared draft (memo co-editing). */
export const DealScreeningMemoDraftSchema = z.object({
  public_id: z.string(),
  base_run_id: z.string().nullable(),
  base_version: z.number().nullable(),
  saved_run_id: z.string().nullable(),
  saved_version: z.number().nullable(),
  seeded_by_name: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DealScreeningMemoDraft = z.infer<typeof DealScreeningMemoDraftSchema>;

export const DealScreeningRunVersionSchema = z.object({
  public_id: z.string(),
  version: z.number(),
  status: DealScreeningRunStatusSchema,
  generated_at: z.string().nullable(),
  generation_error: z.string().nullable(),
  created_at: z.string(),
  counts: CountsSchema,
  /** The deal's name as this version carries it (DOP-659). */
  deal_name: z.string().nullable().optional(),
  origin: z.string().optional(),
  name: z.string().nullable().optional(),
  based_on_version: z.number().nullable().optional(),
  saved_by_name: z.string().nullable().optional(),
  /** Who saved it, who ran it, and who typed what in a version saved from
   * the shared draft (memo co-editing). */
  saved_by_user_id: z.string().nullable().optional(),
  generated_by_user_id: z.string().nullable().optional(),
  generated_by_name: z.string().nullable().optional(),
  contributors: z.array(DealScreeningContributorSchema).optional(),
});
export type DealScreeningRunVersion = z.infer<
  typeof DealScreeningRunVersionSchema
>;

// ----------------------------------------------------------------- payloads

/** Keys, titles, kinds and formulas are not editable; the server ignores them. */
export interface DealScreeningSectionEdit {
  brief?: string;
  appears?: string;
  sub_headings?: string[];
  enabled?: boolean;
}

export interface DealScreeningPolicyEdit {
  policy?: DealScreeningPolicy;
  enabled?: boolean;
}

/** A deal-specific section a person adds: a title and its brief. */
export interface DealScreeningNewDealSpecificSection {
  title: string;
  brief: string;
}

/** Keyed by section / field / figure key. */
export interface DealScreeningRecipeEdits {
  sections?: Record<string, DealScreeningSectionEdit>;
  header?: Record<string, DealScreeningPolicyEdit>;
  figures?: Record<string, DealScreeningPolicyEdit>;
  add_deal_specific_sections?: DealScreeningNewDealSpecificSection[];
  /** Workflow-level conventions: `tone`, `historical_years`, `forecast_years`. */
  conventions?: Record<string, string>;
}

export interface DealScreeningCreateWorkflowPayload {
  name: string;
}

export interface DealScreeningUpdateWorkflowPayload {
  name?: string;
  recipe_edits?: DealScreeningRecipeEdits;
}

export interface DealScreeningCreateDealPayload {
  name: string;
  folder_id: number;
  /** Dispatch the first run as part of the create. */
  run_now: boolean;
}

export interface DealScreeningUpdateDealPayload {
  name?: string;
  folder_id?: number;
  excluded_section_keys?: string[];
}

/**
 * A person's answer for a fact. `accept_na` keeps "Not found in provided
 * materials" in the memo and only lifts the block; a `value` is stored as
 * entered-by with the note as its source.
 */
export interface DealScreeningSetFactPayload {
  period?: string | null;
  value?: number | string | null;
  note: string;
  accept_na?: boolean;
  /** The document the value came from (DOP-652). */
  source?: DealScreeningDocumentSource | null;
}

/**
 * A place in a document that words or a value came from (DOP-652): a page
 * with the passage on it, or a workbook cell. It is a claim: the server
 * re-reads the file when the version is saved and builds the source the memo
 * shows. `filename` is only for showing the source before it is saved, and
 * is never sent.
 */
export interface DealScreeningDocumentSource {
  attachment_id: number;
  page?: number | null;
  sheet?: string | null;
  cell?: string | null;
  quote?: string;
  filename?: string | null;
}

/**
 * `null` for both keeps the values and leaves the conflict flagged. The anchor
 * names the cell or page chosen: two disagreeing cells can sit in one file.
 */
/** One op of a section edit script (DOP-580). */
export interface DealScreeningEditOp {
  /** "title" renames the section and carries only `title` (DOP-641). */
  op: "insert" | "replace" | "delete" | "title";
  title?: string;
  anchor?: string | null;
  after?: string | null;
  at_end?: boolean;
  paragraph?: {
    lead?: string | null;
    sub_heading?: string | null;
    kind?: "prose" | "bullets" | "table" | "chart";
    sentences: string[];
    /** A table as the person left it (DOP-637): headings, cell texts, cell notes. */
    table?: DealScreeningEditOpTable | null;
    /** A chart as given (DOP-655), with where its values came from. */
    chart?: DealScreeningEditOpChart | null;
    /** Where the person's words came from (DOP-637). */
    notes?: DealScreeningSourceNote[];
    /** A bullets paragraph numbered 1, 2, 3 (DOP-641). */
    ordered?: boolean;
    /** Formatting, each a run of one sentence (DOP-641). */
    formats?: {
      sentence: number;
      start: number;
      end: number;
      style: DealScreeningFormatStyle;
    }[];
  } | null;
}

/** A table as an edit op sends it (DOP-637). */
export interface DealScreeningEditOpTable {
  caption: string | null;
  columns: string[];
  rows: string[][];
  notes?: {
    row: number;
    column: number;
    note: string;
    source?: DealScreeningDocumentSource;
  }[];
}

export interface DealScreeningEditOpChart {
  chart_type: string;
  title: string | null;
  data: Record<string, unknown>;
  notes?: { note: string; source?: DealScreeningDocumentSource }[];
}

/**
 * A person's words and where they came from: a note (DOP-637), a document
 * (DOP-652), or both.
 */
export interface DealScreeningSourceNote {
  quote: string;
  note: string;
  source?: DealScreeningDocumentSource;
}

/** A table in an edit op (DOP-637). */
export interface DealScreeningEditTable {
  caption: string | null;
  columns: string[];
  rows: string[][];
  /** Where a typed cell came from, by "row:column" (DOP-637). */
  notes?: Record<string, string>;
  /** The document a cell came from, by "row:column" (DOP-652). */
  sources?: Record<string, DealScreeningDocumentSource>;
}

/** A row a person adds to one deal's financial summary (DOP-637). */
export interface DealScreeningCustomFigure {
  key: string;
  label: string;
  unit: "usd_m" | "pct" | "x" | "count" | "per_share";
  periods: "by_year" | "point";
}

/** A value typed into the financial summary, with where it came from (DOP-637). */
export interface DealScreeningFactEntry {
  key: string;
  period: string | null;
  value: number;
  note: string;
  /** The document the value came from (DOP-652). */
  source?: DealScreeningDocumentSource;
}

/** A company fact or the tagline typed in edit mode, with where it came from (DOP-641). */
export interface DealScreeningHeaderEntry {
  key: string;
  period: null;
  value: string | number;
  note: string;
  /** The document the value came from (DOP-652). */
  source?: DealScreeningDocumentSource;
}

export interface DealScreeningSectionEditPayload {
  ops: DealScreeningEditOp[];
  base_body_sha: string;
}

/** Edits across the memo, saved as the deal's next version (DOP-621). */
export interface DealScreeningSaveVersionPayload {
  name?: string | null;
  edits: (DealScreeningSectionEditPayload & { section_key: string })[];
  /** Copy the version unchanged as the newest one (DOP-623). */
  restore?: boolean;
  /** The financial summary's edits (DOP-637) and the company facts' (DOP-641). */
  custom_figures?: DealScreeningCustomFigure[];
  facts?: (DealScreeningFactEntry | DealScreeningHeaderEntry)[];
  /** The company facts block's heading, renamed (DOP-641). */
  company_facts_title?: string;
  /** The deal renamed with the version, in one transaction (DOP-641). */
  deal_name?: string;
  /** The financial summary's columns (by name) and rows (by key) as dragged (DOP-649). */
  financial_order?: { columns?: string[]; rows?: string[] };
  /** The deal's newest version as the page has it: 409 when another was saved since (DOP-653). */
  expected_latest_version?: number;
  /** Sections a person added, and sections of theirs taken out (DOP-656). */
  new_sections?: DealScreeningNewSection[];
  removed_sections?: string[];
}

/** A section a person adds (DOP-656): after the section named (null: last), written from additions. */
export interface DealScreeningNewSection {
  title: string;
  after: string | null;
  ops: DealScreeningEditOp[];
}

/** Back to the system's value for one fact (DOP-577). */
export interface DealScreeningClearFactPayload {
  period?: string | null;
}

export interface DealScreeningResolveConflictPayload {
  period?: string | null;
  chosen_attachment_id: number | null;
  chosen_anchor?: string | null;
}

export interface DealScreeningReviewPayload {
  note?: string | null;
}

// ----------------------------------------------------------------- parsing

export class DealScreeningMalformedResponseError extends Error {
  constructor(what: string, issues: string) {
    super(`Malformed ${what} response: ${issues}`);
    this.name = "DealScreeningMalformedResponseError";
  }
}

function parseWith<T>(schema: z.ZodType<T>, what: string, raw: unknown): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    throw new DealScreeningMalformedResponseError(what, result.error.message);
  }
  return result.data;
}

export const parseDealScreeningWorkflow = (
  raw: unknown,
): DealScreeningWorkflow =>
  parseWith(DealScreeningWorkflowSchema, "workflow", raw);
export const parseDealScreeningWorkflowList = (
  raw: unknown,
): DealScreeningWorkflowListItem[] =>
  parseWith(z.array(DealScreeningWorkflowListItemSchema), "workflow list", raw);
export const parseDealScreeningDeal = (raw: unknown): DealScreeningDeal =>
  parseWith(DealScreeningDealSchema, "deal", raw);
export const parseDealScreeningDealList = (raw: unknown): DealScreeningDeal[] =>
  parseWith(z.array(DealScreeningDealSchema), "deal list", raw);
export const parseDealScreeningFolderCandidateList = (
  raw: unknown,
): DealScreeningFolderCandidate[] =>
  parseWith(
    z.array(DealScreeningFolderCandidateSchema),
    "folder candidates",
    raw,
  );
export const parseDealScreeningRun = (raw: unknown): DealScreeningRun =>
  parseWith(DealScreeningRunSchema, "run", raw);
export const parseDealScreeningRunSummaryList = (
  raw: unknown,
): DealScreeningRunSummary[] =>
  parseWith(z.array(DealScreeningRunSummarySchema), "run list", raw);
export const parseDealScreeningRunRequest = (
  raw: unknown,
): DealScreeningRunRequest =>
  parseWith(DealScreeningRunRequestSchema, "run request", raw);
export const parseDealScreeningMemoDraft = (raw: unknown): DealScreeningMemoDraft =>
  DealScreeningMemoDraftSchema.parse(raw);
export const parseDealScreeningRunVersionList = (
  raw: unknown,
): DealScreeningRunVersion[] =>
  parseWith(z.array(DealScreeningRunVersionSchema), "run versions", raw);

// ----------------------------------------------------------------- helpers

/**
 * Why "Mark reviewed" is refused right now, or null when it is allowed.
 *
 * The server refuses with 409 for the same two reasons; stating them here lets
 * the button say so in its title instead of letting the user find out by
 * clicking.
 */
export function reviewBlockedReason(
  run: Pick<
    DealScreeningRunSummary,
    "generating" | "blocking_open" | "status"
  > & {
    sections?: RequiredSectionFields[];
  },
): string | null {
  if (run.generating) return "This memo is still being generated";
  if (run.blocking_open > 0) {
    return `Resolve ${run.blocking_open} blocking gap${
      run.blocking_open === 1 ? "" : "s"
    } first`;
  }
  // The server refuses review on these too, naming them (DOP-657).
  const unwritten = requiredSectionsMissing(run.sections ?? []);
  if (unwritten.length > 0) {
    return `Write ${unwritten.map((section) => section.title).join(" and ")} in edit mode first: ${
      unwritten.length === 1 ? "it is" : "they are"
    } set to appear in every memo`;
  }
  return null;
}

type RequiredSectionFields = Pick<
  DealScreeningSection,
  "key" | "appears" | "title" | "written_by" | "deal_specific"
> & {
  paragraphs: Pick<
    DealScreeningParagraph,
    "lead" | "sentences" | "table" | "chart"
  >[];
  required_unwritten?: boolean;
};

/**
 * Whether a paragraph says anything: a lead-in or a sentence with words, a
 * table, or a chart. The same rule as the backend's `edits.has_content`.
 */
const hasText = (
  paragraph: Pick<
    DealScreeningParagraph,
    "lead" | "sentences" | "table" | "chart"
  >,
): boolean =>
  (paragraph.lead ?? "").trim() !== "" ||
  paragraph.sentences.some((sentence) => sentence.text.trim() !== "") ||
  paragraph.table != null ||
  paragraph.chart != null;

/** What the detail of a required section says when a person emptied it. */
export const EMPTIED_IN_EDIT_MODE = "Every paragraph was removed in edit mode";

/**
 * Sections set to appear in every memo whose body says nothing (DOP-657).
 * Each blocks review, as `RunService.review` has it. `paragraphs` is the body
 * as shown, the generated one with the active edit applied, so:
 *  - an edit that only renames an omitted section leaves it unwritten: what
 *    counts is a paragraph with content, not that an edit exists;
 *  - a section the run WROTE and a person then emptied in edit mode is
 *    unwritten too, whatever its stored status (DOP-659).
 * Only the sections the model writes are held to it, as the backend's
 * `always_sections` has it: the financial summary and the questions are
 * built from the facts and carry no paragraphs by design, and a section
 * added for one deal is not the recipe's promise.
 * The server says the same thing as `required_unwritten` (DOP-657); when a
 * run carries it, that answer wins, so the gate here can never disagree with
 * the 409. The rule below is the fallback for runs read before it existed.
 */
export function requiredSectionsMissing<T extends RequiredSectionFields>(
  sections: T[],
): T[] {
  return sections.filter((section) =>
    typeof section.required_unwritten === "boolean"
      ? section.required_unwritten
      : section.appears === "always" &&
        section.written_by !== "code" &&
        section.key !== "diligence_questions" &&
        !section.deal_specific &&
        !section.paragraphs.some(hasText),
  );
}

/** Blocking fields plus required sections still to write: the one blocking number. */
export const blockingTotal = (
  run: Pick<DealScreeningRunSummary, "blocking_open"> & {
    sections?: RequiredSectionFields[];
  },
): number =>
  run.blocking_open + requiredSectionsMissing(run.sections ?? []).length;

/**
 * The title a memo version shows (DOP-659): the deal's name as that version
 * was saved with it, so a rename saved later does not retitle it, else the
 * deal's current name.
 */
export const memoDealName = (run: {
  deal_name?: string | null;
  deal: { name: string };
}): string => run.deal_name?.trim() || run.deal.name;
